import React, { useRef, useState } from 'react'
import { t } from 'i18next'
import { useDispatch, useSelector } from 'react-redux'
import NoImage from 'Assets/Images/Logos/no-image.png'
import { formatterCurrency } from 'Utils/functions'
import { processCatalogBatchFileAction } from 'redux/actions/quickListCatalogActions'
import CatalogProductImageModal from './CatalogProductImageModal'
import {
  CATALOG_CSV_FILE_NAME,
  buildCatalogCsvTemplate,
  getCatalogBatchToast,
  getCatalogProductImageUrl,
  isValidSpecialPrice,
} from '../utils/quickListCatalog.utils'

const downloadTextFile = (content, fileName) => {
  const blob = new Blob([`\uFEFF${content}`], {
    type: 'text/csv;charset=utf-8;',
  })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

// latestValuesRef viene de QuickListCatalog: sigue actualizado aunque este paso se desmonte
// mientras termina una subida (p. ej. si el usuario cambia de paso).
function CatalogProductsStep({
  formik,
  entityId,
  catalogId,
  onBulkUpdated,
  onNotify,
  latestValuesRef,
}) {
  const dispatch = useDispatch()
  const userEmail = useSelector(
    state => state.login?.user?.AuthenticationInfo?.Email || ''
  )
  const { products, sections } = formik.values
  const csvInputRef = useRef(null)
  const [editingProductId, setEditingProductId] = useState(null)
  const [csvResult, setCsvResult] = useState(null)
  const [isProcessingCsv, setIsProcessingCsv] = useState(false)
  // La carga masiva actualiza el catálogo guardado, por eso requiere que ya exista.
  const canUseCsv = Boolean(catalogId)

  const sectionNames = new Map(
    sections.map(section => [section.catalogSectionId, section.name])
  )
  const editingProduct = products.find(
    product => product.productId === editingProductId
  )

  // Actualiza un producto sobre los valores actuales del formulario: la subida de una imagen
  // termina después y no debe revertir precios escritos mientras subía.
  const updateProduct = (productId, changes) => {
    if (!productId) return
    formik.setFieldValue(
      'products',
      latestValuesRef.current.products.map(product =>
        product.productId === productId ? { ...product, ...changes } : product
      )
    )
  }

  const notifyResult = result => {
    const { messageKey, params, severity } = getCatalogBatchToast(result)
    onNotify?.(t(messageKey, params), severity)
  }

  const handleCsvFile = async event => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file || !canUseCsv) return

    if (!file.name.toLowerCase().endsWith('.csv')) {
      const result = { failed: true, fileError: 'CSV_INVALID_FILE', errors: [] }
      setCsvResult(result)
      notifyResult(result)
      return
    }

    setIsProcessingCsv(true)
    setCsvResult(null)

    try {
      const result = await dispatch(
        processCatalogBatchFileAction({ file, email: userEmail })
      )
      setCsvResult(result)
      if (result.processedRowsQty > 0) {
        const refreshed = await onBulkUpdated?.()
        if (refreshed === false) return
      }
      notifyResult(result)
    } finally {
      setIsProcessingCsv(false)
    }
  }

  return (
    <div className="catalog-step" data-testid="container-step-products">
      <div className="catalog-csv">
        <div>
          <h3 className="catalog-subtitle">
            {t('QUICK_LIST_CATALOG.PRODUCTS.CSV_TITLE')}
          </h3>
          <p className="catalog-help">
            {canUseCsv
              ? t('QUICK_LIST_CATALOG.PRODUCTS.CSV_HELP')
              : t('QUICK_LIST_CATALOG.PRODUCTS.CSV_SAVE_FIRST')}
          </p>
        </div>
        <div className="catalog-csv-actions">
          <button
            type="button"
            className="btn-cancel"
            disabled={!canUseCsv || isProcessingCsv}
            onClick={() =>
              downloadTextFile(
                buildCatalogCsvTemplate(products, catalogId),
                CATALOG_CSV_FILE_NAME
              )
            }
            data-testid="btn-download-csv-template">
            {t('QUICK_LIST_CATALOG.PRODUCTS.DOWNLOAD_TEMPLATE')}
          </button>
          <input
            ref={csvInputRef}
            type="file"
            accept=".csv,text/csv"
            className="catalog-hidden-input"
            onChange={handleCsvFile}
            data-testid="input-file-csv"
          />
          <button
            type="button"
            className="btn-add"
            disabled={!canUseCsv || isProcessingCsv}
            aria-busy={isProcessingCsv}
            onClick={() => csvInputRef.current?.click()}
            data-testid="btn-upload-csv">
            {isProcessingCsv
              ? t('QUICK_LIST_CATALOG.PRODUCTS.CSV_PROCESSING')
              : t('QUICK_LIST_CATALOG.PRODUCTS.UPLOAD_CSV')}
          </button>
        </div>

        {csvResult && (
          <div
            className="catalog-csv-result"
            role="status"
            data-testid="container-csv-result-catalog">
            {csvResult.failed ? (
              <>
                <p className="catalog-text-error">
                  {csvResult.fileError
                    ? t(`QUICK_LIST_CATALOG.VALIDATION.${csvResult.fileError}`)
                    : t('QUICK_LIST_CATALOG.PRODUCTS.CSV_FAIL')}
                </p>
                {csvResult.errors.length > 0 && (
                  <ul>
                    {csvResult.errors.map(message => (
                      <li key={message}>{message}</li>
                    ))}
                  </ul>
                )}
              </>
            ) : (
              <>
                <p>
                  {t('QUICK_LIST_CATALOG.PRODUCTS.CSV_RESULT', {
                    processed: csvResult.processedRowsQty,
                    read: csvResult.readRowsQty,
                  })}
                </p>
                {csvResult.processedRowsQty < csvResult.readRowsQty && (
                  <p className="catalog-help">
                    {t('QUICK_LIST_CATALOG.PRODUCTS.CSV_SKIPPED')}
                  </p>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <div className="quick-list-section-table-wrapper">
        <table className="quick-list-section-table catalog-products-table">
          <thead>
            <tr>
              <th>{t('QUICK_LIST_CATALOG.PRODUCTS.IMAGE')}</th>
              <th>{t('QUICK_LIST_CATALOG.PRODUCTS.NAME')}</th>
              <th>{t('QUICK_LIST_CATALOG.PRODUCTS.SKU')}</th>
              <th>{t('QUICK_LIST_CATALOG.PRODUCTS.SECTION')}</th>
              <th>{t('QUICK_LIST_CATALOG.PRODUCTS.PRICE')}</th>
              <th>{t('QUICK_LIST_CATALOG.PRODUCTS.SPECIAL_PRICE')}</th>
            </tr>
          </thead>
          <tbody>
            {products.map(product => {
              const hasValidSpecialPrice = isValidSpecialPrice(
                product.specialPrice
              )
              return (
                <tr
                  key={product.productId}
                  data-testid={`item-product-${product.productId}`}>
                  <td>
                    <div className="catalog-product-image">
                      <img
                        src={getCatalogProductImageUrl(product) || NoImage}
                        alt={product.productName || ''}
                        width={48}
                        height={48}
                        className="quick-list-section-thumb"
                      />
                      {product.customImageUrl && (
                        <span className="catalog-gallery-badge">
                          {t('QUICK_LIST_CATALOG.PRODUCTS.CUSTOM_IMAGE')}
                        </span>
                      )}
                      <button
                        type="button"
                        className="catalog-link-button"
                        onClick={() => setEditingProductId(product.productId)}
                        data-testid={`btn-change-image-${product.productId}`}>
                        {t('QUICK_LIST_CATALOG.PRODUCTS.CHANGE_IMAGE')}
                      </button>
                    </div>
                  </td>
                  <td data-testid={`text-product-name-${product.productId}`}>
                    {product.productName}
                  </td>
                  <td data-testid={`text-product-sku-${product.productId}`}>
                    {product.sku}
                  </td>
                  <td data-testid={`text-product-section-${product.productId}`}>
                    {sectionNames.get(product.catalogSectionId) ||
                      t('QUICK_LIST_CATALOG.PRODUCTS.UNASSIGNED')}
                  </td>
                  <td data-testid={`text-product-price-${product.productId}`}>
                    {product.price !== null && product.price !== undefined
                      ? `$${formatterCurrency(product.price)}`
                      : t('QUICK_LIST_CATALOG.PRODUCTS.PRICE_ON_SAVE')}
                  </td>
                  <td>
                    <input
                      type="text"
                      inputMode="decimal"
                      className={
                        hasValidSpecialPrice
                          ? 'form-control catalog-price-input'
                          : 'form-control catalog-price-input error'
                      }
                      value={product.specialPrice}
                      placeholder="0.00"
                      onChange={event =>
                        updateProduct(product.productId, {
                          specialPrice: event.target.value,
                        })
                      }
                      data-testid={`input-special-price-${product.productId}`}
                    />
                    {!hasValidSpecialPrice && (
                      <div className="input-feedback">
                        <strong
                          data-testid={`text-error-special-price-${product.productId}`}>
                          {t(
                            'QUICK_LIST_CATALOG.VALIDATION.INVALID_SPECIAL_PRICE'
                          )}
                        </strong>
                      </div>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <CatalogProductImageModal
        product={editingProduct}
        entityId={entityId}
        onClose={() => setEditingProductId(null)}
        // El id se fija al iniciar la subida, aunque el modal se cierre antes de terminar.
        onChange={url =>
          updateProduct(editingProduct?.productId, { customImageUrl: url })
        }
      />
    </div>
  )
}

export default CatalogProductsStep
